import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';

// Componentes de UI
import LoginScreen from './components/ui/LoginScreen';
import Modal from './components/ui/Modal';
import PackageForm from './components/ui/PackageForm';
import AIAssistant from './components/ui/AIAssistant';

// Módulos
import RegistroGeneral from './components/modules/RegistroGeneral';
import ControlMensajero from './components/modules/ControlMensajero';
import Validaciones from './components/modules/Validaciones';
import ControlFinanciero from './components/modules/ControlFinanciero';
import UsuariosPermisos from './components/modules/UsuariosPermisos';
import KPIsModule from './components/modules/KPIs';
import InventarioModule from './components/modules/Inventario';

// --- CONFIGURACIÓN Y CONSTANTES (Sin cambios) ---
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

export const appId = 'gestor-paquetes-avanzado';
export const logoUrl = process.env.PUBLIC_URL + '/aralellum.png';

export const MODULES = {
    REGISTRO_GENERAL: "Registro General de Pedidos",
    CONTROL_MENSAJERO: "Control de Pedidos por Mensajero",
    VALIDACIONES: "Validación de Errores y Repetidos",
    CONTROL_FINANCIERO: "Control Financiero",
    KPIS: "KPIs / Índices",
    INVENTORY: "Gestión de Inventario",
    USUARIOS_PERMISOS: "Usuarios y Permisos",
    GENERAL: "Permisos Generales",
};

export const PERMISSIONS_STRUCTURE = {
    REGISTRO_GENERAL: { view: 'Ver Módulo', create: 'Crear Paquetes', delete: 'Eliminar Paquetes', upload: 'Subir CSV' },
    CONTROL_MENSAJERO: { view: 'Ver Módulo', edit: 'Editar Paquetes' },
    VALIDACIONES: { view: 'Ver Módulo', resolve: 'Resolver Errores' },
    CONTROL_FINANCIERO: { view: 'Ver Módulo', manage_expenses: 'Gestionar Egresos' },
    KPIS: { view: 'Ver Módulo' },
    INVENTORY: {
        view: 'Ver Módulo',
        manage_products: 'Gestionar Productos y Diseños',
        manage_locations: 'Gestionar Ubicaciones (Admin)',
        create_transfer: 'Crear Traslados',
        approve_transfer: 'Aprobar Traslados',
        manage_production: 'Gestionar Producción',
        create_exit: 'Registrar Salidas Finales',
    },
    USUARIOS_PERMISOS: { view: 'Ver Módulo', manage: 'Gestionar Usuarios' },
    GENERAL: { ask_ai_permission: 'Usar Asistente de IA' },
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

function App() {
    const [currentModule, setCurrentModule] = useState(null);
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentPackage, setCurrentPackage] = useState(null);
    const [isEditingFromCourierModule, setIsEditingFromCourierModule] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', message: '', actions: null });
    
    const closeModal = useCallback(() => setIsModalOpen(false), []);
    const showModal = useCallback((title, message, actions = null) => {
        setModalContent({ title, message, actions });
        setIsModalOpen(true);
    }, []);

    useEffect(() => {
        setIsAuthReady(false);
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const userDocRef = doc(db, 'users', firebaseUser.uid);
                try {
                    const docSnap = await getDoc(userDocRef);
                    if (docSnap.exists()) {
                        const profileData = docSnap.data();

                        if (profileData.accountStatus === 'suspendido') {
                            await signOut(auth); 
                            showModal("Cuenta Suspendida", "Tu cuenta ha sido suspendida. Por favor, contacta a un administrador.");
                            setUser(null);
                            setUserProfile(null);
                            setIsAuthReady(true);
                            return; 
                        }

                        setUser(firebaseUser);
                        setUserProfile({ uid: firebaseUser.uid, ...profileData });

                    } else {
                        await signOut(auth);
                    }
                } catch (error) {
                    console.error("Error al cargar perfil:", error);
                    await signOut(auth);
                }
            } else {
                setUser(null);
                setUserProfile(null);
            }
            setIsAuthReady(true);
        });
        return () => unsubscribe();
    }, [showModal]);

    const handleLogout = async () => {
        setCurrentModule(null);
        await signOut(auth);
    };

    const handleUpdatePackage = async (packageId, packageData) => {
        setIsLoading(true);
        try {
            const packageDocRef = doc(db, `artifacts/${appId}/packages`, packageId);
            const parseDateString = (dateString) => {
                if (!dateString || typeof dateString !== 'string') return null;
                const parts = dateString.split('-');
                if (parts.length !== 3) return null;
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const day = parseInt(parts[2], 10);
                const date = new Date(year, month, day);
                if (isNaN(date.getTime()) || date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
                    return null;
                }
                return date;
            };
            
            const shipDateObject = parseDateString(packageData.shipDate);
            let deliveryDateObject = null;

            if (isEditingFromCourierModule) {
                deliveryDateObject = new Date();
            } else {
                deliveryDateObject = parseDateString(packageData.deliveryDate);
            }

            const dataToUpdate = {
                ...packageData,
                productValue: parseFloat(packageData.productValue) || 0,
                shippingCost: parseFloat(packageData.shippingCost) || 0,
                expenses: parseFloat(packageData.expenses) || 0,
                shipDate: shipDateObject ? Timestamp.fromDate(shipDateObject) : null,
                deliveryDate: deliveryDateObject ? Timestamp.fromDate(deliveryDateObject) : null,
                updatedAt: Timestamp.now(),
            };
            
            delete dataToUpdate.id;
            if (dataToUpdate.shipDateForForm) delete dataToUpdate.shipDateForForm;
            if (dataToUpdate.deliveryDateForForm) delete dataToUpdate.deliveryDateForForm;

            await updateDoc(packageDocRef, dataToUpdate);
            showModal("Éxito", "Paquete actualizado correctamente.");
            setIsEditModalOpen(false);
            setCurrentPackage(null);
        } catch (error) {
            console.error("Error al actualizar paquete: ", error);
            showModal("Error", `No se pudo actualizar el paquete: ${error.message}`);
        }
        setIsLoading(false);
    };

    const openFormForEdit = (pkg, fromCourier = false) => {
        setIsEditingFromCourierModule(fromCourier);
        setCurrentPackage(pkg);
        setIsEditModalOpen(true);
    };

    const renderModuleContent = () => {
        const props = {
            showModal, closeModal, db, auth,
            userId: user?.uid,
            permissions: userProfile?.permissions || {},
            currentUser: userProfile,
            openFormForEdit, handleUpdatePackage,
        };

        switch (currentModule) {
            case MODULES.REGISTRO_GENERAL: return <RegistroGeneral {...props} />;
            case MODULES.CONTROL_MENSAJERO: return <ControlMensajero {...props} />;
            case MODULES.VALIDACIONES: return <Validaciones {...props} />;
            case MODULES.CONTROL_FINANCIERO: return <ControlFinanciero {...props} />;
            case MODULES.KPIS: return <KPIsModule {...props} />;
            case MODULES.INVENTORY: return <InventarioModule {...props} />;
            case MODULES.USUARIOS_PERMISOS: return <UsuariosPermisos {...props} />;
            default:
                return (
                    <div key="welcome-screen" className="text-center py-10 bg-white rounded-lg shadow">
                        <h2 className="mt-4 text-2xl font-semibold text-gray-700">Bienvenid@, {userProfile?.name || user?.email}</h2>
                        <p className="mt-2 text-gray-500">Selecciona un módulo del menú superior para comenzar.</p>
                    </div>
                );
        }
    };

    if (!isAuthReady) {
        return <div className="flex justify-center items-center min-h-screen bg-gray-100"><p>Cargando...</p></div>;
    }

    // --- CAMBIO ESTRUCTURAL ---
    // El contenedor principal ahora envuelve toda la lógica de renderizado
    // para que los modales siempre estén disponibles.
    return (
        <div className="container mx-auto p-4 md:p-8 font-inter bg-gray-50 min-h-screen">
            
            {user && userProfile ? (
                // --- VISTA PARA USUARIOS AUTENTICADOS ---
                <>
                    <header className="mb-8 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <img src={logoUrl} alt="Logo de la Empresa" className="h-12 w-auto" />
                            <h1 className="text-3xl md:text-4xl font-bold text-sky-700">Gestor de Envíos y Finanzas</h1>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-600">{userProfile?.name || user?.email}</p>
                            <button onClick={handleLogout} className="text-sm text-sky-600 hover:text-sky-800 hover:underline">Cerrar Sesión</button>
                        </div>
                    </header>
                    <nav className="mb-8 flex justify-center flex-wrap gap-2 sm:gap-4 border-b pb-3">
                        {Object.entries(MODULES).map(([key, moduleName]) => {
                            if (userProfile?.permissions?.[key]?.view) {
                                return (
                                    <button
                                        key={key}
                                        onClick={() => setCurrentModule(moduleName)}
                                        className={`py-2 px-3 sm:px-4 rounded-md text-sm sm:text-base font-medium transition-colors mb-2
                                            ${currentModule === moduleName ? 'bg-sky-600 text-white shadow-md' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                                        {moduleName}
                                    </button>
                                );
                            }
                            return null;
                        })}
                    </nav>
                    
                    {renderModuleContent()}

                    {userProfile && <AIAssistant showModal={showModal} currentUser={userProfile} />}
                </>
            ) : (
                // --- VISTA PARA USUARIOS NO AUTENTICADOS (PANTALLA DE LOGIN) ---
                <LoginScreen auth={auth} showModal={showModal} />
            )}

            {/* Los modales se mueven aquí, fuera de la lógica condicional, para que siempre estén disponibles */}
            <Modal isOpen={isEditModalOpen} onClose={() => { setIsEditModalOpen(false); setCurrentPackage(null); setIsEditingFromCourierModule(false); }}>
                {currentPackage && <PackageForm onSubmit={(data) => handleUpdatePackage(currentPackage.id, data)} onCancel={() => { setIsEditModalOpen(false); setCurrentPackage(null); }} initialData={currentPackage} isLoading={isLoading} showAppModal={showModal} isEditingFromCourierModule={isEditingFromCourierModule}/>}
            </Modal>
            <Modal isOpen={isModalOpen} onClose={closeModal} title={modalContent.title}>
                <p className="text-gray-700 mb-4">{modalContent.message}</p>
                {modalContent.actions ? <div className="flex justify-end">{modalContent.actions}</div> : <button onClick={closeModal} className="btn-primary w-full">Entendido</button>}
            </Modal>

            <style jsx global>{`
              .btn-primary { padding: 0.75rem 1.5rem; background-color: #0284C7; color: white; border-radius: 0.5rem; font-weight: 600; transition: background-color 0.15s ease-in-out; }
              .btn-primary:hover { background-color: #0369A1; } .btn-primary:disabled { background-color: #7DD3FC; opacity: 0.7; cursor: not-allowed; }
              .btn-secondary { padding: 0.75rem 1.5rem; background-color: #E5E7EB; color: #374151; border-radius: 0.5rem; transition: background-color 0.15s ease-in-out; }
              .btn-secondary:hover { background-color: #D1D5DB; } .btn-secondary:disabled { opacity: 0.5; }
              .btn-success { padding: 0.5rem 1rem; background-color: #16A34A; color: white; border-radius: 0.5rem; font-weight: 600; transition: background-color 0.15s ease-in-out; }
              .btn-success:hover { background-color: #15803D; }
              .input-field { width: 100%; padding: 0.75rem; border: 1px solid #D1D5DB; border-radius: 0.5rem; transition: box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out; }
              .input-field:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 2px #BFDBFE; }
            `}</style>
        </div>
    );
}

export default App;